// Los 12 desenlaces del comprobante de domicilio, traducidos a lo que el vendedor tiene que HACER.
//
// 🔴 Ninguno se pinta como un renglón vacío ni como un "listo" genérico: el vendedor lo lee como que
// el domicilio ya quedó y sigue con la venta. Cada estado dice qué pasó y qué sigue.
import { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import type { KycAttemptProgress, ProofAddressResultStatus, ProofOfAddressResult } from './types';

/**
 * Qué se hace con el resultado. Es la agrupación que importa: cuatro desenlaces distintos para doce
 * motivos.
 *
 * - `SAVED` — quedó texto de domicilio en el expediente. Si ESO alcanza para la cuenta lo dice el
 *   silo (`addressRequired`), no este mapa: los `HELP_NEEDED` guardan sin resolver a calle.
 * - `RETRY_DOCUMENT` — el recibo no sirvió: hay que pedirle otro al cliente.
 * - `NEEDS_REVIEW` — fuera de nuestro alcance o esperando a que alguien lo mire. No se promete nada.
 * - `RETRY_SYSTEM` — falló algo nuestro; se puede reintentar.
 */
export type ProofAddressOutcome = 'SAVED' | 'RETRY_DOCUMENT' | 'NEEDS_REVIEW' | 'RETRY_SYSTEM';

export type ProofAddressTone = 'success' | 'warning' | 'danger';

export interface ProofAddressFeedback {
    outcome: ProofAddressOutcome;
    tone: ProofAddressTone;
    title: string;
    detail: string;
}

/**
 * Un mensaje por estado. El `Record` cerrado obliga a decidir el copy de cada valor nuevo.
 *
 * 🔴 El cliente lee esta pantalla de frente: ningún mensaje nombra el área que revisa ni la causa
 * — ver `copyPolicy`. Exportado porque el guardián de `blocked.test.ts` lo recorre.
 */
export const PROOF_ADDRESS_FEEDBACK: Record<ProofAddressResultStatus, ProofAddressFeedback> = {
    SUCCESS: {
        outcome: 'SAVED',
        tone: 'success',
        title: 'Comprobante aceptado',
        detail: 'El domicilio del recibo quedó guardado en el expediente del cliente.',
    },
    MEX_HELP_NEEDED: {
        outcome: 'SAVED',
        tone: 'warning',
        title: 'Domicilio guardado, pero sin la calle exacta',
        detail: 'Se guardó el texto del recibo y queda en revisión adicional, pero así no completa el expediente: la cuenta necesita el domicilio ubicado a nivel calle. Pídele al cliente otro comprobante más legible o con la dirección completa.',
    },
    USA_HELP_NEEDED: {
        outcome: 'SAVED',
        tone: 'warning',
        title: 'Domicilio de Estados Unidos guardado, pero sin la calle exacta',
        detail: 'Se guardó el texto del recibo y queda en revisión adicional, pero así no completa el expediente: la cuenta necesita el domicilio ubicado a nivel calle. Pídele al cliente otro comprobante más legible o con la dirección completa.',
    },
    NO_ADDRESS_DETECTED: {
        outcome: 'RETRY_DOCUMENT',
        tone: 'warning',
        title: 'No se pudo leer un domicilio en el comprobante',
        detail: 'La foto está ilegible o el documento no es un comprobante de domicilio. Pídele otro recibo al cliente y vuelve a lanzarlo.',
    },
    PLACES_NO_MATCH: {
        outcome: 'RETRY_DOCUMENT',
        tone: 'warning',
        title: 'No se encontró ese domicilio',
        detail: 'El domicilio del recibo no coincide con ninguna dirección conocida. Pídele otro comprobante, de preferencia reciente y con la dirección completa.',
    },
    COUNTRY_MISMATCH: {
        outcome: 'NEEDS_REVIEW',
        tone: 'warning',
        title: 'El domicilio no coincide con el país que declaró el cliente',
        detail: 'El recibo es de otro país, así que queda en revisión adicional: no lo des por registrado. Pídele al cliente un comprobante de su domicilio en México.',
    },
    COUNTRY_NOT_SUPPORTED: {
        outcome: 'NEEDS_REVIEW',
        tone: 'warning',
        title: 'Domicilio de un país que no manejamos',
        detail: 'Solo se pueden usar comprobantes de México o Estados Unidos, así que este no completa el expediente. Pídele al cliente otro recibo de alguno de esos dos países.',
    },
    PENDING_METAMAP_REVIEW: {
        outcome: 'NEEDS_REVIEW',
        tone: 'warning',
        title: 'El comprobante está en revisión del proveedor',
        detail: 'Una persona está revisando el documento y todavía no hay resultado: no lo des por registrado. En cuanto llegue aparece aquí. Si prefieres no esperar, pídele otro comprobante al cliente.',
    },
    METAMAP_ERROR: {
        outcome: 'RETRY_SYSTEM',
        tone: 'danger',
        title: 'No se pudo traer el comprobante del proveedor',
        detail: 'Falló la conexión al leer el documento. Intenta de nuevo: el cliente tiene que volver a subir el recibo.',
    },
    PLACES_ERROR: {
        outcome: 'RETRY_SYSTEM',
        tone: 'danger',
        title: 'No se pudo ubicar el domicilio',
        detail: 'El servicio de direcciones no respondió. Intenta de nuevo en un momento.',
    },
    ADDRESS_SAVE_ERROR: {
        outcome: 'RETRY_SYSTEM',
        tone: 'danger',
        title: 'No se pudo guardar el domicilio',
        detail: 'El comprobante se leyó bien, pero falló al guardarlo en el expediente. Intenta de nuevo; si vuelve a fallar, repórtalo a soporte.',
    },
    // Relanzar puede devolver `alreadyHasAddress` y no marcar el requisito: por eso el detalle manda
    // a soporte además de ofrecer el reintento.
    CHECKBOX_ERROR: {
        outcome: 'NEEDS_REVIEW',
        tone: 'warning',
        title: 'El domicilio quedó guardado, pero el requisito no se marcó',
        detail: 'El domicilio del cliente ya está en su expediente; lo que falló fue marcar el requisito como cumplido. Puedes continuar con la venta y repórtalo a soporte con el número de esta sesión.',
    },
    UNKNOWN: {
        outcome: 'NEEDS_REVIEW',
        tone: 'warning',
        title: 'No sabemos cómo quedó el comprobante',
        detail: 'El proveedor devolvió un resultado que esta pantalla no conoce. Pídele otro comprobante al cliente y repórtalo a soporte.',
    },
};

/** Qué decirle al vendedor sobre este resultado. */
export function proofOfAddressFeedback(status: ProofAddressResultStatus): ProofAddressFeedback {
    return PROOF_ADDRESS_FEEDBACK[status];
}

/**
 * Etiqueta del botón que vuelve a lanzar el comprobante; `null` cuando el silo ya dio el requisito
 * por cumplido y relanzar no ayuda.
 *
 * 🔴 Cuelga de `addressRequired`, NO del desenlace. Los dos `HELP_NEEDED` son `SAVED` y aun así el
 * silo sigue pidiendo domicilio: con el botón atado al desenlace, esa pantalla obligatoria quedaba
 * sin salida. El reintento no tiene tope — el paso no tiene salida lateral.
 */
export function proofOfAddressRetryLabel(
    outcome: ProofAddressOutcome,
    addressRequired: boolean,
): string | null {
    if (!addressRequired) return null;
    if (outcome === 'RETRY_SYSTEM') return 'Intentar de nuevo';
    return 'Pedir otro comprobante';
}

/** El veredicto del intento vigente: `null` mientras ese intento no llega a RESULT. */
export function attemptProofResult(
    result: ProofOfAddressResult | null | undefined,
    progress: KycAttemptProgress | null,
): ProofOfAddressResult | null {
    if (!result) return null;
    // Sin intento (sesión previa al despliegue) se usa el veredicto tal cual.
    if (progress === null) return result;
    return progress.stage === KycAttemptStageEnum.RESULT ? result : null;
}

/**
 * ¿El comprobante sigue abierto y hay que esperar otra notificación?
 *
 * Manda el `terminal` del silo, que es quien conoce el ciclo de vida de cada status. El mapa local
 * solo entra cuando el campo no viaja — ver `proofAwaitsVerdict`.
 */
export function proofStillOpen(result: ProofOfAddressResult): boolean {
    if (result.terminal !== undefined) return !result.terminal;
    return proofAwaitsVerdict(result.status);
}

/**
 * TRANSITORIO — fallback de la ventana de despliegue. Es la copia local de una terminalidad que hoy
 * vive del lado del productor: se usa solo mientras un wizard sin `terminal` pueda responderle a un
 * front nuevo. Cuando ese despliegue esté parejo en todos los ambientes, esto se borra.
 *
 * 🔴 `PENDING_METAMAP_REVIEW` no es un desenlace: es "lo está mirando una persona". Tratarlo como
 * cierre apaga el poll, y entonces el veredicto real no puede llegar nunca a esta pantalla — que es
 * justo lo que la entrega por cola vino a evitar.
 */
export function proofAwaitsVerdict(status: ProofAddressResultStatus): boolean {
    return status === 'PENDING_METAMAP_REVIEW';
}
